/**
 * Zod schemas for the auth payloads (handoff §1, §3.2).
 *
 * These are the single source of truth for the shape: the TypeScript types are
 * *derived* from them, so a type can never drift from what we validate — the
 * exact failure mode that made the previous app render blank tables.
 */
import { z } from "zod";

/** Stable role codes (handoff §2). Branch logic on these, display role_label. */
export const roleCodeSchema = z.enum([
  "super_admin",
  "tenant_admin",
  "org_admin",
  "viewer",
  "org_auditor"
]); 
export type RoleCode = z.infer<typeof roleCodeSchema>;

export const scopeLevelSchema = z.enum(["platform", "group", "org"]);
export type ScopeLevel = z.infer<typeof scopeLevelSchema>;

/** Renameable display terms (handoff §3.5). */
export const labelsSchema = z.object({
  tier2_singular: z.string().default("Partner"),
  tier2_plural: z.string().default("Partners"),
  readonly_role: z.string().default("Analyst"),
});
export type Labels = z.infer<typeof labelsSchema>;

/** The seven module toggles (handoff §4). Missing key = enabled. */
export const modulesSchema = z.object({
  sysmon: z.boolean(),
  mitre: z.boolean(),
  vuln: z.boolean(),
  threat: z.boolean(),
  compliance: z.boolean(),
  fim: z.boolean(),
  cis: z.boolean(),
}).partial();
export type Modules = z.infer<typeof modulesSchema>;

export const moduleEnforcementSchema = z.enum(["restricted", "unrestricted"]);
export type ModuleEnforcement = z.infer<typeof moduleEnforcementSchema>;

export const userSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: roleCodeSchema,
  role_label: z.string(),
  scope_level: scopeLevelSchema,
  group_id: z.string().nullable(),
  org_id: z.string().nullable(),
  /** Human-readable org name. null for platform/group admins. */
  org_name: z.string().nullable().default(null),
  /** Human-readable group name. null for platform admins and org users. */
  group_name: z.string().nullable().default(null),
  permissions: z.array(z.string()),
  labels: labelsSchema,
  /** null for platform/group admins, who see every module (handoff §1). */
  modules: modulesSchema.nullable().default(null),
  /** "unrestricted" = module checks are skipped for this user. */
  module_enforcement: moduleEnforcementSchema.default("unrestricted"),
});
export type AuthUser = z.infer<typeof userSchema>;

export const loginResponseSchema = z.object({
  access_token: z.string(),
  token_type: z.string(),
  user: userSchema,
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;
